import type {
  Item,
  Menu,
  MenuTpa,
  Section,
  SectionTpa,
  PopulatedMenu,
  PopulatedSection,
} from '../../types/menusTypes';
import type { MenusAvailabilityStatus, Money, TimeSlotsPerMenu } from '../../types/businessTypes';
import type { PopulatedItemTpa } from 'root/types/item';
import {
  AvailabilityStatus,
  type MenuAvailabilityStatus,
} from '@wix/ambassador-restaurants-menu-settings-v1-menu-ordering-settings/types';
import type {
  FirstFulfillmentTimeSlotsPerMenu,
  FulfillmentTimeSlot,
} from '@wix/ambassador-restaurants-operations-v1-operation/types';

export const convertPopulatedItemsToItemType = ({
  items,
  currency,
  imagesData = [],
}: {
  items: PopulatedItemTpa[];
  currency: string;
  imagesData?: { id: string; width: number; height: number }[];
}): Item[] => {
  const updatedItems: Item[] = [];
  items.forEach((item) => {
    const price: Money = {
      amount: Number(item.priceInfo?.price) || 0,
      currency,
    };

    if (item.image && item.image.id && !(item.image.width || item.image.height)) {
      const imgData = imagesData.find((img) => img.id === item.image?.id);

      item.image.width = item.image.width || imgData?.width;
      item.image.height = item.image.height || imgData?.height;
    }

    if (item.id) {
      updatedItems.push({
        id: item.id,
        name: item.name,
        description: item.description,
        price,
        priceVariants: item.priceVariants,
        labels: item.labels,
        image: item.image,
        additionalImages: item.additionalImages,
        visible: !!item.visible,
        orderSettings: {
          inStock: item.orderSettings?.inStock ?? true,
          taxGroupId: item.orderSettings?.taxGroupId,
          acceptSpecialRequests: item.orderSettings?.acceptSpecialRequests,
        },
        modifierGroups: item.modifierGroups,
        revision: item.revision || '0',
      });
    }
  });

  return updatedItems;
};

export const convertSectionsToSectionType = ({
  sections,
}: {
  sections: SectionTpa[];
}): Section[] => {
  const updatedSections: Section[] = [];
  sections.forEach((section) => {
    if (section.id) {
      updatedSections.push({
        id: section.id,
        name: section.name,
        description: section.description,
        image: section.image,
        additionalImages: section.additionalImages,
        visible: section.visible !== false,
        itemIds: section.itemIds || [],
        revision: section.revision || '0',
      });
    }
  });

  return updatedSections;
};

export const convertMenusToMenuType = ({ menus = [] }: { menus?: MenuTpa[] }): Menu[] => {
  const updatedMenus: Menu[] = [];
  menus.forEach((menu) => {
    if (menu.id) {
      updatedMenus.push({
        id: menu.id,
        name: menu.name,
        description: menu.description,
        visible: !!menu.visible,
        sectionIds: menu.sectionIds || [],
        revision: menu.revision || '0',
      });
    }
  });

  return updatedMenus;
};

export const populateMenu = (menu: Menu, sections: Section[]): PopulatedMenu => {
  const sectionMap = new Map();

  sections.forEach((section) => sectionMap.set(section.id, section));
  const sectionIds = [...new Set(menu.sectionIds ?? [])].filter((sectionId) =>
    sectionMap.has(sectionId)
  );

  const menuSections = sectionIds.map((sectionId) => sectionMap.get(sectionId));

  const visibleMenuSections = menuSections.filter((section) => section.visible);

  let size = 0;

  const dedupedSections = visibleMenuSections.map((section) => {
    const itemIds = [...new Set(section.itemIds ?? [])];
    size += section.itemIds.length ?? 0;
    return {
      ...section,
      itemIds,
      size: section.itemIds.length,
    };
  }) as PopulatedSection[];

  return {
    ...menu,
    sectionIds,
    sections: dedupedSections,
    size,
  };
};

export const convertDate = (date: Date) => {
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
  };
};

export const createTimeSlotId = (startTime: Date, endTime: Date) =>
  `${startTime?.toUTCString()}-${endTime?.toUTCString()}`;

export const convertMenuAvailabilityStatusArrayToKeyValue = (
  menuStatuses: MenuAvailabilityStatus[] = []
): MenusAvailabilityStatus => {
  return menuStatuses.reduce((acc, status) => {
    if (status.menuId) {
      acc[status.menuId] = status.availabilityStatus ?? AvailabilityStatus.AVAILABLE;
    }
    return acc;
  }, {} as MenusAvailabilityStatus);
};

export const convertTimeSlotsPerMenuArrayToKeyValue = (
  timeSlotsPerMenu: FirstFulfillmentTimeSlotsPerMenu[] = []
) => {
  return timeSlotsPerMenu.reduce<TimeSlotsPerMenu>((menuAcc, menu) => {
    const menuId = menu.menuId ?? '';
    const timeSlotMap = (menu.timeslotsPerFulfillmentType ?? []).reduce<
      Record<string, FulfillmentTimeSlot>
    >((slotAcc, slot) => {
      const key = slot.fulfilmentType?.toLowerCase() ?? '';
      if (key) {
        slotAcc[key] = slot;
      }
      return slotAcc;
    }, {});
    if (menuId) {
      menuAcc[menuId] = timeSlotMap;
    }
    return menuAcc;
  }, {});
};

export const getTimezoneOffset = (timeZone: string, date = new Date()) => {
  const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
  const tzDate = new Date(date.toLocaleString('en-US', { timeZone }));
  return (tzDate.getTime() - utcDate.getTime()) / (60 * 6e4);
};

export const getItemIds = (menus: PopulatedMenu[]) => {
  const sections = menus.map((menu) => menu.sections).flat();
  return Array.from(
    sections?.reduce<Set<string>>((acc, section) => {
      section.itemIds?.forEach((itemId) => acc.add(itemId));
      return acc;
    }, new Set<string>()) ?? []
  );
};
